import {
  Button,
  ClipboardIcon,
  Flex,
  Icon,
  IconButton,
  Menu,
  Spacer,
  Text,
  toaster,
} from 'components'
import { useWorkspace } from 'components/providers'
import { CollectionName, DCTableName } from 'types'
import { isProduction, useEntevatePermission } from 'utils'

const cloudBaseUrl = 'https://console.cloud.google.com'
const dbUrl = `${cloudBaseUrl}/firestore/databases/-default-/data/panel`
const dcUrl = `https://console.firebase.google.com/u/0/project/entevate/dataconnect/locations/us-central1/services/momentify/data`
const storageFileUrl = `${cloudBaseUrl}/storage/browser/_details/entevate.appspot.com`
const storageFolderUrl = `${cloudBaseUrl}/storage/browser/entevate.appspot.com`

export interface Props {
  items: DevItem[]
}

type DevItem = DBDevItem | DCDevItem | StorageDevItem

interface DBDevItem {
  collection: CollectionName
  id?: string
  label: string
  type: 'db'
}

interface DCDevItem {
  table: DCTableName
  id?: string
  label: string
  type: 'dc'
}

interface StorageDevItem {
  label: string
  type: 'storage'
  isFolder?: boolean
  url?: string
}

export default function DevDetailsButton({ items }: Props) {
  const [entevatePermission] = useEntevatePermission()
  const { workspace } = useWorkspace()

  if (entevatePermission !== 'entevate' || isProduction()) return null

  const withUrls = items
    .map(item => {
      if (item.type === 'db') {
        if (!item.id) return null
        return { ...item, url: `${dbUrl}/${item.collection}/${item.id}` }
      } else if (item.type === 'dc') {
        if (!item.id) return null
        return { ...item, url: dcUrl }
      } else if (item.type === 'storage') {
        if (!item.url) return null
        const baseUrl = item.isFolder ? storageFolderUrl : storageFileUrl
        return { ...item, url: `${baseUrl}/Workspaces/${workspace?.id}/${item.url}` }
      }
    })
    .filter(Boolean) as Array<DevItem & { url: string }>

  return (
    <Menu
      hideCheckbox
      as={IconButton}
      menuProps={{ positioning: { placement: 'bottom-end' } }}
      variant="ghost"
      buttonLabel={<Icon>bug_report</Icon>}
      onChange={async v => {
        const item = withUrls.find(i => i.label === v)
        if (!item) return

        if (item.type === 'dc') {
          await navigator.clipboard.writeText(item.id ?? '')
          toaster.create({
            title: (
              <Flex align="center" justify="space-between">
                <Text>ID copied</Text>
                <Button
                  size="sm"
                  onClick={e => {
                    e.stopPropagation()
                    window.open(item.url, '_blank')?.focus()
                  }}
                >
                  Open Console
                </Button>
              </Flex>
            ),
            type: 'success',
          })
        } else {
          window.open(item.url, '_blank')?.focus()
        }
      }}
      options={withUrls.map(item => {
        return {
          value: item.label,
          label: (
            <Flex flex={1} alignItems="center" gap={2}>
              <Icon>{item.type === 'storage' ? 'folder' : 'database'}</Icon>
              {item.label}
              {item.type === 'db' && (
                <>
                  <Spacer />
                  <Flex
                    p={1}
                    _hover={{ bg: 'primary.300' }}
                    onClick={e => {
                      e.stopPropagation()
                      navigator.clipboard.writeText(item.id ?? '')
                      toaster.create({ title: 'ID copied', type: 'success' })
                    }}
                    borderRadius="xs"
                  >
                    <ClipboardIcon type="outline" />
                  </Flex>
                </>
              )}
            </Flex>
          ),
        }
      })}
    />
  )
}
